import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Star, RotateCcw, Volume2, ArrowRight } from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const SortingGame = ({ 
  items = [], 
  categories = [],
  onComplete, 
  title = "Sort the items!",
  instructions = "Drag each item to the correct category"
}) => {
  const [unsortedItems, setUnsortedItems] = useState([]);
  const [sortedItems, setSortedItems] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    initializeGame();
  }, [items, categories]);

  const initializeGame = () => {
    // Shuffle items
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    setUnsortedItems(shuffled);
    
    // Initialize empty categories
    const initialSorted = {};
    categories.forEach(cat => {
      initialSorted[cat.id] = [];
    });
    setSortedItems(initialSorted);
    setScore(0);
    setAttempts(0);
    setCompleted(false);
  };

  const speakText = (text) => {
    _speakTTS(text, 0.85);
  };

  const handleDragStart = (item) => {
    setDraggedItem(item);
    speakText(item.label);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const handleDrop = (categoryId) => {
    const item = draggedItem || selectedItem;
    if (!item) return;
    
    setAttempts(a => a + 1);
    
    // Check if correct
    const isCorrect = item.categoryId === categoryId;
    
    if (isCorrect) {
      setScore(s => s + 10);
      toast.success('Correct!', { duration: 1000 });
      
      // Add to sorted items
      setSortedItems(prev => ({
        ...prev,
        [categoryId]: [...prev[categoryId], item]
      }));
      
      // Remove from unsorted
      setUnsortedItems(prev => prev.filter(i => i.id !== item.id));
      
      // Check completion
      if (unsortedItems.length === 1) {
        handleGameComplete();
      }
    } else {
      toast.error('Try again! That doesn\'t belong there.', { duration: 1500 });
    }
    
    setDraggedItem(null);
    setSelectedItem(null);
  };

  // Tap-to-select fallback for touch devices (mobile/tablet) where native HTML5 drag doesn't fire
  const handleItemTap = (item) => {
    speakText(item.label);
    setSelectedItem((prev) => (prev && prev.id === item.id) ? null : item);
  };

  const handleGameComplete = () => {
    setCompleted(true);
    const finalScore = Math.round((score + 10) / (items.length * 10) * 100);
    if (onComplete) {
      onComplete(finalScore, attempts + 1);
    }
  };

  const getCategoryColor = (index) => {
    const colors = ['bg-emerald-100 border-emerald-300', 'bg-amber-100 border-amber-300', 'bg-cyan-100 border-cyan-300', 'bg-pink-100 border-pink-300'];
    return colors[index % colors.length];
  };

  if (completed) {
    const finalScore = Math.round(score / (items.length * 10) * 100);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
        data-testid="sorting-complete"
      >
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 flex items-center justify-center">
          <Star className="w-12 h-12 text-white" />
        </div>
        <h2 className="font-fredoka text-3xl font-bold text-emerald-800 mb-2">All Sorted!</h2>
        <p className="text-xl text-emerald-600 mb-2">Score: {finalScore}%</p>
        <p className="text-lg text-slate-500 mb-6">Completed in {attempts} attempts</p>
        <Button onClick={initializeGame} variant="outline" className="gap-2" data-testid="play-again-btn">
          <RotateCcw className="w-4 h-4" /> Play Again
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sorting-game">
      {/* Header */}
      <div className="text-center">
        <h3 className="font-fredoka text-2xl font-bold text-slate-800 mb-2">{title}</h3>
        <p className="text-slate-600">{instructions}</p>
        <div className="flex justify-center gap-4 mt-2">
          <span className="text-sm text-slate-500">Items left: {unsortedItems.length}</span>
          <span className="text-sm text-emerald-600 font-medium">Score: {score}</span>
        </div>
      </div>

      {/* Categories (Drop Zones) */}
      <div className="grid grid-cols-2 gap-4">
        {categories.map((category, idx) => (
          <div
            key={category.id}
            className={`min-h-32 rounded-2xl border-3 border-dashed p-4 transition-all cursor-pointer ${getCategoryColor(idx)} ${
              (draggedItem || selectedItem) ? 'ring-2 ring-offset-2 ring-slate-400' : ''
            }`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(category.id)}
            onClick={() => selectedItem && handleDrop(category.id)}
            data-testid={`category-${category.id}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{category.icon}</span>
              <h4 className="font-bold text-slate-800">{category.label}</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              <AnimatePresence>
                {sortedItems[category.id]?.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="px-3 py-2 bg-white rounded-lg shadow-sm border border-slate-200 text-sm font-medium"
                  >
                    {item.label}
                    <CheckCircle className="w-3 h-3 inline ml-1 text-emerald-500" />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>

      {/* Unsorted Items */}
      <div className="bg-slate-50 rounded-2xl p-6">
        <p className="text-sm text-slate-500 mb-4 text-center">Tap an item then tap a category (or drag on desktop):</p>
        <div className="flex flex-wrap gap-3 justify-center">
          <AnimatePresence>
            {unsortedItems.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5 }}
                draggable
                onDragStart={() => handleDragStart(item)}
                onDragEnd={handleDragEnd}
                className={`px-5 py-3 rounded-xl font-bold cursor-grab active:cursor-grabbing select-none transition-all ${
                  (draggedItem?.id === item.id) || (selectedItem?.id === item.id)
                    ? 'bg-emerald-500 text-white scale-110 shadow-lg ring-2 ring-emerald-300'
                    : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-emerald-300 hover:shadow-md'
                }`}
                onClick={() => handleItemTap(item)}
                data-testid={`item-${item.id}`}
              >
                {item.label}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Reset Button */}
      <div className="text-center">
        <Button variant="outline" size="sm" onClick={initializeGame} className="gap-2" data-testid="reset-btn">
          <RotateCcw className="w-4 h-4" /> Start Over
        </Button>
      </div>
    </div>
  );
};

export default SortingGame;
